import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2 } from '@angular/core'
import { filter, Subscription } from 'rxjs'
import { EventService } from './services/event.service'
import { NavigationEnd, Router } from '@angular/router'

interface Position {
  x: number
  y: number
  top: number
  bottom: number
  left: number
  right: number
  centerX: number
  centerY: number
}

@Directive({
  selector: '[appButtonController]',
  standalone: true,
})
export class HTVButtonControllerDirective implements OnDestroy, AfterViewInit {
  routerSubscription?: Subscription
  eventSubscription?: Subscription

  selectedButton?: any
  focusedElement?: any
  scrollableElement?: any

  readonly selectableClass = 'htv-selectable'
  readonly selectedClass = 'htv-selected'
  readonly focusClass = 'htv-focus'
  readonly autoclickClass = 'htv-autoclick'
  readonly autofocusClass = 'htv-autofocus'
  readonly scrollableClass = 'htv-scrollable'
  readonly forceDirectionClass = 'htv-force-direction'
  readonly containClass = 'htv-contain'

  initTimer: any
  readonly initTimerDelayInMilliseconds = 100

  readonly scrollOffset = 50

  constructor(
    private el: ElementRef,
    private eventService: EventService,
    private renderer: Renderer2,
    private router: Router
  ) {
    // do nothing
  }

  ngAfterViewInit(): void {
    this.registerRouterListener()
    this.registerEventListener()
    this.initSelectedButton()
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe()
    this.eventSubscription?.unsubscribe()
  }

  registerRouterListener(): void {
    this.routerSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.forceRefreshSelectedButton()
    })
  }

  registerEventListener(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe((key: string) => {
      if (key == 'ENTER') {
        const selectedButtons = this.el.nativeElement.getElementsByClassName(this.selectedClass)
        if (selectedButtons.length <= 0) {
          return
        }

        const selectedButton = selectedButtons[0]
        if (selectedButton.tagName === 'INPUT') {
          if (this.focusedElement == selectedButton) {
            selectedButton.blur()
            this.focusedElement = undefined
          } else {
            selectedButton.focus()
            this.focusedElement = selectedButton
          }
        } else {
          selectedButton.click()
        }
      }

      if (key.substring(0, 5) == 'ARROW') {
        let nextButton: any = null

        const selectedButtons = this.el.nativeElement.getElementsByClassName(this.selectedClass)
        if (selectedButtons.length <= 0) {
          const button = this.getInitialButton(true)
          if (button) {
            nextButton = button
          }
        } else {
          if (this.scrollableElement) {
            switch (key) {
              case 'ARROW-UP': {
                if (this.scrollableElement.scrollTop > 0) {
                  this.scrollableElement.scrollTop -= this.scrollOffset
                  return
                }
                break
              }
              case 'ARROW-DOWN': {
                if (
                  this.scrollableElement.offsetHeight + this.scrollableElement.scrollTop <
                  this.scrollableElement.scrollHeight
                ) {
                  this.scrollableElement.scrollTop += this.scrollOffset
                  return
                }
                break
              }
              default:
                break
            }
          }

          let smallestGap = 99999
          let smallestXGap = 99999
          let smallestYGap = 99999
          const activeButton = selectedButtons[0]
          const activePosition = this.getPosition(activeButton)
          const forceDirection = activeButton.classList.contains(this.forceDirectionClass)
          const buttons = this.getSelectableButtons()

          for (const button of buttons) {
            const position = this.getPosition(button)
            switch (key) {
              case 'ARROW-UP':
                if (position.centerY < activePosition.centerY) {
                  const xgap = Math.abs(position.centerX - activePosition.centerX)
                  const ygap = Math.abs(position.centerY - activePosition.centerY)
                  if (forceDirection) {
                    if (ygap < smallestYGap) {
                      nextButton = button
                      smallestXGap = xgap
                      smallestYGap = ygap
                    } else if (ygap == smallestYGap) {
                      if (xgap < smallestXGap) {
                        nextButton = button
                        smallestXGap = xgap
                      }
                    }
                  } else {
                    const gap = ygap + xgap * 3
                    if (gap < smallestGap) {
                      nextButton = button
                      smallestGap = gap
                    }
                  }
                }
                break
              case 'ARROW-DOWN':
                if (position.centerY > activePosition.centerY) {
                  const xgap = Math.abs(position.centerX - activePosition.centerX)
                  const ygap = Math.abs(position.centerY - activePosition.centerY)
                  if (forceDirection) {
                    if (ygap < smallestYGap) {
                      nextButton = button
                      smallestXGap = xgap
                      smallestYGap = ygap
                    } else if (ygap == smallestYGap) {
                      if (xgap < smallestXGap) {
                        nextButton = button
                        smallestXGap = xgap
                      }
                    }
                  } else {
                    const gap = ygap + xgap * 3
                    if (gap < smallestGap) {
                      nextButton = button
                      smallestGap = gap
                    }
                  }
                }
                break
              case 'ARROW-LEFT':
                if (position.centerX < activePosition.centerX) {
                  const xgap = Math.abs(position.centerX - activePosition.centerX)
                  const ygap = Math.abs(position.centerY - activePosition.centerY)
                  if (forceDirection) {
                    if (position.centerY == activePosition.centerY) {
                      if (xgap < smallestXGap) {
                        nextButton = button
                        smallestXGap = xgap
                      }
                    }
                  } else {
                    const gap = xgap + ygap * 3
                    if (gap < smallestGap) {
                      nextButton = button
                      smallestGap = gap
                    }
                  }
                }
                break
              case 'ARROW-RIGHT':
                if (position.centerX > activePosition.centerX) {
                  const xgap = Math.abs(position.centerX - activePosition.centerX)
                  const ygap = Math.abs(position.centerY - activePosition.centerY)
                  if (forceDirection) {
                    if (position.centerY == activePosition.centerY) {
                      if (xgap < smallestXGap) {
                        nextButton = button
                        smallestXGap = xgap
                      }
                    }
                  } else {
                    const gap = xgap + ygap * 3
                    if (gap < smallestGap) {
                      nextButton = button
                      smallestGap = gap
                    }
                  }
                }
                break
              default:
                break
            }
          }
        }

        if (nextButton != null) {
          this.selectButton(nextButton)
        }
      }
    })
  }

  initSelectedButton() {
    if (this.initTimer) {
      clearTimeout(this.initTimer)
    }
    this.initTimer = setTimeout(() => {
      const button = this.getInitialButton()
      if (button) {
        this.selectButton(button)
      }
    }, this.initTimerDelayInMilliseconds)
  }

  getInitialButton(force = false): object | undefined {
    const buttons = this.getSelectableButtons()
    if (!buttons || buttons.length <= 0) {
      return undefined
    }

    const selectedButtons = this.el.nativeElement.getElementsByClassName(this.selectedClass)
    if (selectedButtons.length > 0) {
      return undefined
    }

    const focusButtons: HTMLElement[] = []
    for (const button of buttons) {
      const priority = this.getFocusPriority(button)
      if (priority !== -Infinity) {
        focusButtons.push(button)
      }
    }

    if (focusButtons.length > 0) {
      focusButtons.sort((a, b) => {
        return this.getFocusPriority(b) - this.getFocusPriority(a)
      })
      return focusButtons[0]
    }

    if (force) {
      return buttons[0]
    }

    return undefined
  }

  getFocusPriority(element: HTMLElement): number {
    const classes = element.classList

    for (let i = 0; i < classes.length; i++) {
      const className = classes[i]
      if (className.startsWith('htv-focus-')) {
        const priority = parseInt(className.substring(10), 10)
        if (!isNaN(priority)) {
          return priority
        }
      }
    }

    if (classes.contains(this.focusClass)) {
      return Infinity
    }

    return -Infinity
  }

  clearSelectedButtons(): void {
    if (this.focusedElement) {
      this.focusedElement.blur()
      this.focusedElement = undefined
    }
    if (this.scrollableElement) {
      this.scrollableElement = undefined
    }
    this.selectedButton = undefined
    const selectedButtons = this.el.nativeElement.getElementsByClassName(this.selectedClass)
    if (selectedButtons.length > 0) {
      for (let i = selectedButtons.length - 1; i >= 0; i--) {
        this.renderer.removeClass(selectedButtons[i], this.selectedClass)
      }
    }
  }

  forceRefreshSelectedButton(): void {
    this.clearSelectedButtons()
    this.initSelectedButton()
  }

  selectButton(button: any): void {
    this.clearSelectedButtons()
    this.renderer.addClass(button, this.selectedClass)
    this.selectedButton = button

    const activeElement = this.el.nativeElement.querySelector(`.${this.selectedClass}`) as HTMLElement
    if (activeElement) {
      const hasScrollableClass = activeElement.classList.contains(this.scrollableClass)
      if (hasScrollableClass) {
        this.scrollableElement = activeElement
        return
      }

      const supportsScrollIntoViewOptions = 'scrollBehavior' in document.documentElement.style
      if (supportsScrollIntoViewOptions) {
        activeElement.scrollIntoView({ block: 'center', inline: 'center' })
      }

      const container = this.findScrollableParent(activeElement)
      if (container) {
        const elementRect = activeElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const style = window.getComputedStyle(container)
        const padTop = parseFloat(style.paddingTop) || 0
        const padBottom = parseFloat(style.paddingBottom) || 0
        const padLeft = parseFloat(style.paddingLeft) || 0
        const padRight = parseFloat(style.paddingRight) || 0

        const visibleTop = containerRect.top + padTop
        const visibleBottom = containerRect.bottom - padBottom
        const visibleLeft = containerRect.left + padLeft
        const visibleRight = containerRect.right - padRight

        const buffer = 20

        if (elementRect.top < visibleTop + buffer) {
          container.scrollTop -= visibleTop + buffer - elementRect.top
        } else if (elementRect.bottom > visibleBottom - buffer) {
          container.scrollTop += elementRect.bottom - (visibleBottom - buffer)
        }

        if (elementRect.left < visibleLeft + buffer) {
          container.scrollLeft -= visibleLeft + buffer - elementRect.left
        } else if (elementRect.right > visibleRight - buffer) {
          container.scrollLeft += elementRect.right - (visibleRight - buffer)
        }
      }
    }

    if (activeElement.classList.contains(this.autoclickClass)) {
      activeElement.click()
    }

    if (activeElement.classList.contains(this.autofocusClass)) {
      activeElement.focus()
      this.focusedElement = activeElement
    }
  }

  findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent: HTMLElement | null = element.parentElement
    while (parent) {
      const overflowY = window.getComputedStyle(parent).overflowY
      const overflowX = window.getComputedStyle(parent).overflowX
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') {
        return parent
      }
      parent = parent.parentElement
    }
    return null
  }

  getPosition(button: any): Position {
    const position = button.getBoundingClientRect()
    return {
      x: position.left,
      y: position.top,
      top: position.top,
      bottom: position.bottom,
      left: position.left,
      right: position.right,
      centerX: position.left + Math.abs((position.right - position.left) / 2),
      centerY: position.top + Math.abs((position.bottom - position.top) / 2),
    }
  }

  getSelectableButtons(): any {
    const containerElement = this.el.nativeElement.querySelector(`.${this.containClass}`)

    if (containerElement) {
      return containerElement.getElementsByClassName(this.selectableClass)
    } else {
      return this.el.nativeElement.getElementsByClassName(this.selectableClass)
    }
  }
}
